import { ChevronLeft, ChevronRight, LogOut, Menu, X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { cn } from "@repo/utils";
import { Avatar, AvatarFallback } from "./avatar";
import { Button } from "./button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";
import logoCorail from "./assets/corail-horizontal.svg";
import logoCorailMarque from "./assets/corail-marque.svg";

export interface SidebarItem {
  /** Cible de navigation, telle qu'attendue par `renderLink`. */
  to: string;
  label: string;
  /** Icône lucide, déjà instanciée par l'appelant. */
  icon: ReactNode;
  /** L'entrée est-elle celle de la page courante ? */
  actif?: boolean;
}

export interface SidebarProps {
  items: SidebarItem[];
  /**
   * Rendu d'un lien. La sidebar vit dans `@repo/ui`, qui ne dépend pas de
   * react-router : c'est l'app qui fournit son `<Link>`. `onNavigate` referme
   * le tiroir mobile et doit être branché sur le clic.
   */
  renderLink: (args: {
    to: string;
    className: string;
    children: ReactNode;
    onNavigate: () => void;
    /**
     * À poser sur l'élément rendu. Replié, le libellé disparaît du DOM et
     * l'icône est `aria-hidden` : sans cet attribut le lien n'aurait plus
     * AUCUN nom accessible (le tooltip Radix décrit, il ne nomme pas).
     */
    ariaLabel?: string;
  }) => ReactNode;
  /** Logo de l'organisation. Absent → seul le nom est affiché. */
  logoUrl?: string;
  /** Nom de l'organisation, en `alt` du logo et en légende. */
  organisationNom?: string;
  userInfo?: {
    displayName: string;
    subtitle: string;
    initials: string;
  };
  onLogout?: () => void;
  isLoggingOut?: boolean;
  /**
   * Encart libre rendu entre la bande organisation et la navigation (solde de
   * crédits, alerte…). Reçoit `replie` pour que l'appelant choisisse sa forme
   * courte : `@repo/ui` n'a pas à connaître ce qu'on y affiche.
   */
  encart?: (args: { replie: boolean }) => ReactNode;
  /**
   * Clé de mémorisation de l'état replié, propre à chaque app. Absente →
   * l'état n'est pas conservé d'une visite à l'autre.
   */
  clePersistance?: string;
}

const LARGEUR_DEPLIEE = "w-64";
const LARGEUR_REPLIEE = "w-16";

/**
 * L'état replié est un confort d'affichage propre à CE navigateur : il n'a rien
 * à faire en base. Les accès sont gardés — `localStorage` lève en navigation
 * privée ou quand les données de site sont bloquées, et la sidebar doit se
 * rendre quand même.
 */
function lireEtatReplie(cle: string | undefined): boolean {
  if (!cle) return false;
  try {
    return window.localStorage.getItem(cle) === "1";
  } catch {
    return false;
  }
}

function ecrireEtatReplie(cle: string | undefined, replie: boolean): void {
  if (!cle) return;
  try {
    window.localStorage.setItem(cle, replie ? "1" : "0");
  } catch {
    // Stockage indisponible : l'état reste valable pour la session en cours.
  }
}

export function Sidebar({
  items,
  renderLink,
  logoUrl,
  organisationNom,
  userInfo,
  onLogout,
  isLoggingOut = false,
  encart,
  clePersistance,
}: SidebarProps) {
  const [ouvertMobile, setOuvertMobile] = useState(false);
  // Initialiseur paresseux : la lecture du stockage ne se rejoue pas à chaque
  // rendu.
  const [replie, setReplie] = useState(() => lireEtatReplie(clePersistance));
  const fermer = () => setOuvertMobile(false);

  const basculerRepli = () => {
    setReplie((precedent) => {
      const suivant = !precedent;
      ecrireEtatReplie(clePersistance, suivant);
      return suivant;
    });
  };

  /**
   * `estReplie` ne vaut vrai que pour la variante desktop : le tiroir mobile
   * s'ouvre PAR-DESSUS le contenu, l'y replier ne libérerait aucune place.
   */
  const contenu = (estReplie: boolean) => (
    <div className="flex h-full flex-col gap-2 overflow-y-auto overflow-x-hidden">
      {/* Identité du PRODUIT : toujours le logo Corail RH, jamais celui du
          client. La bande suivante dit chez QUI on est — deux questions
          distinctes, deux surfaces distinctes. */}
      <div className="flex h-16 shrink-0 items-center justify-center border-b border-border px-4">
        <img
          src={estReplie ? logoCorailMarque : logoCorail}
          alt="Corail RH"
          className="h-8 w-auto"
        />
      </div>

      {/* Bande organisation : le contexte courant, là où l'œil le cherche.
          Rendue même sans logo — le nom seul suffit à situer le locataire.
          Repliée, elle garde le logo réduit et laisse tomber le nom, illisible
          sur 4 rem. */}
      {(logoUrl ?? organisationNom) && (
        <div className="flex shrink-0 flex-col items-center gap-2 border-b border-border px-2 py-3 text-center">
          {logoUrl && (
            <img
              src={logoUrl}
              alt={organisationNom ?? "Logo de l'organisation"}
              // Google Drive refuse le hotlink quand le `Referer` est un
              // `http://localhost` (429, puis ERR_BLOCKED_BY_ORB côté navigateur) :
              // sans Referer il répond 200. Vaut pour toutes les balises qui
              // affichent le logo d'une organisation.
              referrerPolicy="no-referrer"
              className={cn(
                "w-auto object-contain",
                estReplie ? "max-h-8 max-w-[2.5rem]" : "max-h-10 max-w-[9rem]",
              )}
            />
          )}
          {!estReplie && organisationNom && (
            <p className="max-w-full truncate text-sm font-medium text-charte-soft">
              {organisationNom}
            </p>
          )}
          {/* Replié et sans logo : deux lettres valent mieux qu'une bande vide. */}
          {estReplie && !logoUrl && organisationNom && (
            <span className="text-xs font-semibold text-charte-soft">
              {organisationNom.slice(0, 2).toUpperCase()}
            </span>
          )}
        </div>
      )}

      {encart && (
        <div
          className={cn(
            "shrink-0 border-b border-border py-2",
            estReplie ? "px-2" : "px-3",
          )}
        >
          {encart({ replie: estReplie })}
        </div>
      )}

      <nav className={cn("flex-1 space-y-1 py-2", estReplie ? "px-2" : "px-3")}>
        {items.map((item) => {
          const lien = renderLink({
            to: item.to,
            onNavigate: fermer,
            ariaLabel: estReplie ? item.label : undefined,
            className: cn(
              "flex items-center rounded-surface py-2 text-sm font-medium transition-colors",
              estReplie ? "justify-center px-2" : "gap-3 px-3",
              item.actif
                ? "bg-charte-wash-accent text-charte-accent"
                : "text-charte-soft hover:bg-charte-hover hover:text-charte-accent",
            ),
            children: (
              <>
                <span className="shrink-0" aria-hidden>
                  {item.icon}
                </span>
                {/* Replié, le libellé est RETIRÉ et non masqué en CSS : un
                    `sr-only` le laisserait dans l'arbre d'accessibilité en plus
                    du tooltip, qui porte déjà le même texte. */}
                {!estReplie && <span className="truncate">{item.label}</span>}
              </>
            ),
          });

          if (!estReplie) return lien;

          return (
            <Tooltip key={item.to}>
              <TooltipTrigger asChild>
                <span className="block">{lien}</span>
              </TooltipTrigger>
              <TooltipContent side="right">{item.label}</TooltipContent>
            </Tooltip>
          );
        })}
      </nav>

      {userInfo && (
        <div
          className={cn(
            "shrink-0 border-t border-border py-3",
            estReplie ? "px-2" : "px-3",
          )}
        >
          <div
            className={cn(
              "flex items-center",
              estReplie ? "justify-center" : "gap-3",
            )}
          >
            <Avatar className="h-8 w-8 shrink-0 bg-primary">
              <AvatarFallback className="bg-primary text-xs text-primary-foreground">
                {userInfo.initials}
              </AvatarFallback>
            </Avatar>
            {!estReplie && (
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">
                  {userInfo.displayName}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {userInfo.subtitle}
                </p>
              </div>
            )}
          </div>

          {onLogout &&
            (estReplie ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Se déconnecter"
                    className="mt-2 w-full text-charte-soft hover:bg-charte-hover hover:text-charte-accent"
                    onClick={onLogout}
                    disabled={isLoggingOut}
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">Se déconnecter</TooltipContent>
              </Tooltip>
            ) : (
              <Button
                variant="ghost"
                className="mt-2 w-full justify-start gap-3 px-3 text-charte-soft hover:bg-charte-hover hover:text-charte-accent"
                onClick={onLogout}
                disabled={isLoggingOut}
              >
                <LogOut className="h-4 w-4" />
                {isLoggingOut ? "Déconnexion…" : "Se déconnecter"}
              </Button>
            ))}
        </div>
      )}
    </div>
  );

  return (
    // `TooltipProvider` local : la sidebar porte ses propres tooltips en mode
    // replié et ne peut pas supposer que l'app en fournit un. Radix accepte
    // l'imbrication de providers.
    <TooltipProvider>
      {/* Barre mobile : la sidebar devient un tiroir sous `md`. `fixed` et non
          `sticky` — en flux, elle deviendrait un item de la rangée flex du
          layout et se placerait À GAUCHE du contenu. Les pages compensent sa
          hauteur par un `pt-14 md:pt-0`. */}
      <div className="fixed inset-x-0 top-0 z-40 flex h-14 items-center gap-2 border-b border-border bg-card px-4 md:hidden">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Ouvrir le menu"
          aria-expanded={ouvertMobile}
          onClick={() => setOuvertMobile(true)}
        >
          <Menu className="h-5 w-5" />
        </Button>
        <img src={logoCorail} alt="Corail RH" className="h-7 w-auto" />
        {organisationNom && (
          <span className="truncate text-sm text-muted-foreground">
            {organisationNom}
          </span>
        )}
      </div>

      <aside
        className={cn(
          "relative hidden shrink-0 border-r border-border bg-card shadow-2xs transition-[width] duration-200 md:sticky md:top-0 md:block md:h-screen",
          replie ? LARGEUR_REPLIEE : LARGEUR_DEPLIEE,
        )}
      >
        {/* Poignée posée SUR la bordure droite : elle ne consomme aucune
            largeur de colonne, ce qui compte quand celle-ci tombe à 4 rem. */}
        <Button
          variant="outline"
          size="icon"
          aria-label={replie ? "Déplier le menu" : "Replier le menu"}
          aria-expanded={!replie}
          onClick={basculerRepli}
          className="absolute -right-3 top-20 z-10 h-6 w-6 rounded-full bg-card p-0 shadow-2xs"
        >
          {replie ? (
            <ChevronRight className="h-3.5 w-3.5" />
          ) : (
            <ChevronLeft className="h-3.5 w-3.5" />
          )}
        </Button>
        {contenu(replie)}
      </aside>

      {ouvertMobile && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Fond cliquable : `button` plutôt qu'une `div` pour rester
              atteignable au clavier et annoncé comme fermeture. */}
          <button
            type="button"
            aria-label="Fermer le menu"
            className="absolute inset-0 bg-charte-strong/40"
            onClick={fermer}
          />
          <div
            className={cn(
              "absolute inset-y-0 left-0 border-r border-border bg-card shadow-lg",
              LARGEUR_DEPLIEE,
            )}
          >
            <Button
              variant="ghost"
              size="icon"
              aria-label="Fermer le menu"
              className="absolute right-2 top-3 z-10"
              onClick={fermer}
            >
              <X className="h-5 w-5" />
            </Button>
            {contenu(false)}
          </div>
        </div>
      )}
    </TooltipProvider>
  );
}
